import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, X, Volume2, RotateCcw, SkipForward } from "lucide-react";
import { createXRStore } from "@react-three/xr";
import { useGame } from "@/hooks/useGame";
import { createAudioEngine } from "@/lib/audioEngine";
import { fetchPovs, fetchDialogue, fetchBibliography, postProgress } from "@/lib/api";
import { FALLBACK_POVS } from "@/lib/narrativeData";
import {
  PHASES, SCENE_MODE, PHASE_TASKS, SIM_TOTAL, SIM_RATE, DECAY_THRESHOLD, formatSim,
} from "@/lib/phases";

import SceneCanvas from "@/components/scene/SceneCanvas";
import LandingCover from "@/components/LandingCover";
import POVPicker from "@/components/POVPicker";
import SurvivalHUD from "@/components/hud/SurvivalHUD";
import ViewControls from "@/components/hud/ViewControls";
import VitalityMeters from "@/components/hud/VitalityMeters";
import MinimapCutaway from "@/components/hud/MinimapCutaway";
import DPad from "@/components/hud/DPad";
import SubtitleRibbon from "@/components/hud/SubtitleRibbon";
import TaskPrompt from "@/components/hud/TaskPrompt";
import FactCheckPanel from "@/components/FactCheckPanel";
import LegacyScreen from "@/components/LegacyScreen";
import FailScreen from "@/components/FailScreen";
import NarrativeGate from "@/components/NarrativeGate";
import PreLaunchPanel from "@/components/PreLaunchPanel";
import LandingAudioControl from "@/components/LandingAudioControl";
import StudyChapters from "@/components/hud/StudyChapters";
import Compass from "@/components/hud/Compass";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMPACT_SEC_OF_DAY = 9 * 3600 + 2 * 60 + 59;
const OFFICE_IMG = "/archival/office_31.jpg";
const WITNESS = { impact: "/audio/brian_impact.mp3", acoustic: "/audio/stanley_witness.mp3" };

function formatClock(secOfDay) {
  let s = Math.floor(secOfDay) % 86400;
  let h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")} ${ampm}`;
}

export default function SpatialNarrativeApp() {
  const game = useGame();
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = createAudioEngine();
  const ttsRef = useRef(null);
  const witnessRef = useRef(null);
  if (!ttsRef.current && typeof Audio !== "undefined") ttsRef.current = new Audio();
  if (!witnessRef.current && typeof Audio !== "undefined") witnessRef.current = new Audio();
  const xrStore = useMemo(() => createXRStore(), []);

  const [screen, setScreen] = useState("landing");
  const [pov, setPov] = useState("brian"); // display pov: stanley | brian | observer
  const [phase, setPhase] = useState("impact");
  const [mode, setMode] = useState("prologue");
  const [viewMode, setViewMode] = useState("first");
  const [xray, setXray] = useState(false);
  const [scrubFloor, setScrubFloor] = useState(42);

  const [povs, setPovs] = useState(FALLBACK_POVS);
  const [biblio, setBiblio] = useState([]);
  const [lines, setLines] = useState([]);
  const [dialogIndex, setDialogIndex] = useState(0);
  const [dialogueDone, setDialogueDone] = useState(false);

  const [remaining, setRemaining] = useState(SIM_TOTAL);
  const [decay, setDecay] = useState(false);
  const [breath, setBreath] = useState(100);
  const [stamina, setStamina] = useState(100);

  const [emitCount, setEmitCount] = useState(0);
  const [pullPct, setPullPct] = useState(0);
  const [aligned, setAligned] = useState(false);
  const [descentFloor, setDescentFloor] = useState(81);
  const [aimNorm, setAimNorm] = useState(0.1);

  const [factOpen, setFactOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = typeof localStorage !== "undefined" ? localStorage.getItem("tab_volume") : null;
    return v != null ? Number(v) : 0.9;
  });
  const [vrSupported, setVrSupported] = useState(false);
  const [redFlash, setRedFlash] = useState(false);
  const [failReason, setFailReason] = useState("collapse");
  const [dustConsume, setDustConsume] = useState(false);
  const [slowmo, setSlowmo] = useState(false);
  const [cinematic, setCinematic] = useState(false);
  const slowmoRef = useRef(false);
  const [collapsing, setCollapsing] = useState(false);
  const [gate, setGate] = useState(null); // null | "sky44" | "call31"
  const [pendingStart, setPendingStart] = useState(null); // crisis pov awaiting pre-launch confirm
  const [studyMode, setStudyMode] = useState(false); // Guided Study: no timer, no collapse
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [hideText, setHideText] = useState(true);
  const [ashWash, setAshWash] = useState(0);
  const [smokeDensity, setSmokeDensity] = useState(0.7); // dial: 70% default
  const manualSmokeRef = useRef(false); // true once the user drags the dial
  const smokeStateRef = useRef(0.7);
  const voicesRef = useRef([]);
  const [descentGuidanceDismissed, setDescentGuidanceDismissed] = useState(false);
  const [tourMode, setTourMode] = useState(false); // Guided Tour: hands-free cinematic narration
  const [menuOpen, setMenuOpen] = useState(false); // central user menu
  const tourRef = useRef(false);
  const tourStepRef = useRef(0);
  useEffect(() => { tourRef.current = tourMode; }, [tourMode]);
  const gate44Ref = useRef(false);
  const gate31Ref = useRef(false);
  // real interview testimony — non-blocking floor-triggered narrative layer
  const testimonyRef = useRef(null);
  if (!testimonyRef.current && typeof Audio !== "undefined") {
    testimonyRef.current = new Audio();
    testimonyRef.current.preload = "auto";
  }
  const brief84Ref = useRef(false);
  const brief44Ref = useRef(false);
  const brief31Ref = useRef(false);
  const activeAudioRef = useRef(null);
  const prologueAdvancedRef = useRef(false);
  const leadTimerRef = useRef(null);
  const observerLeadRef = useRef("stanley"); // Observer POV chains Stanley → Brian lead clips
  const leadSrcRef = useRef(null); // currently-loaded witness clip src (prevents restart on remount)
  const testimonyActiveRef = useRef(false);
  const sirensRef = useRef(null);
  if (!sirensRef.current && typeof Audio !== "undefined") {
    sirensRef.current = new Audio("/audio/sirens.mp3");
    sirensRef.current.loop = true;
    sirensRef.current.preload = "auto";
  }
  const [nowPlaying, setNowPlaying] = useState(null);
  const [captionsOn, setCaptionsOn] = useState(false);
  const gateRef = useRef(null);
  const studyRef = useRef(false);
  const coughLevelRef = useRef(0);
  const coughNextRef = useRef(0);
  const coughElapsedRef = useRef(0);
  useEffect(() => { slowmoRef.current = slowmo; }, [slowmo]);
  useEffect(() => { gateRef.current = gate; }, [gate]);
  useEffect(() => { studyRef.current = studyMode; }, [studyMode]);

  const triggerCollapse = useCallback((reason) => {
    setFailReason(reason || "collapse");
    setCollapsing(true);
    audioRef.current.resume();
    audioRef.current.collapseRumble();
    setTimeout(() => { setCollapsing(false); setScreen("fail"); }, 2300);
  }, []);

  const remainingRef = useRef(SIM_TOTAL);
  const breathRef = useRef(100);
  const staminaRef = useRef(100);
  const runningRef = useRef(false);
  const decayFiredRef = useRef(false);
  const rafRef = useRef(0);
  const lastRef = useRef(0);
  const mirrorRef = useRef(0);
  const pullRafRef = useRef(0);
  const impactRafRef = useRef(0);
  const endedRef = useRef(false);
  const dialogueDoneRef = useRef(false);
  const keyHeldRef = useRef(false);
  const shiftHeldRef = useRef(false);
  const btnHoldRef = useRef(false);
  const linesRef = useRef([]);
  const dialogIndexRef = useRef(0);

  useEffect(() => { dialogueDoneRef.current = dialogueDone; }, [dialogueDone]);
  useEffect(() => { linesRef.current = lines; }, [lines]);
  useEffect(() => { dialogIndexRef.current = dialogIndex; }, [dialogIndex]);

  const interactionPov = pov === "stanley" ? "stanley" : "brian";

  const triggerHaptic = useCallback((strong) => {
    try {
      const s = xrStore.getState && xrStore.getState().session;
      const sources = s && s.inputSources ? s.inputSources : [];
      for (const src of sources) {
        const act = src.gamepad && src.gamepad.hapticActuators && src.gamepad.hapticActuators[0];
        if (act && act.pulse) act.pulse(strong ? 1.0 : 0.7, strong ? 260 : 150);
      }
    } catch (e) {}
  }, [xrStore]);

  // -------- init --------
  useEffect(() => {
    fetchPovs().then(setPovs).catch(() => {});
    fetchBibliography().then(setBiblio).catch(() => {});
    if (navigator.xr && navigator.xr.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-vr").then((s) => setVrSupported(!!s)).catch(() => {});
    }
  }, []);

  // landing → menu
  useEffect(() => {
    if (screen === "landing") {
      const t = setTimeout(() => setScreen("menu"), 2000);
      return () => clearTimeout(t);
    }
  }, [screen]);

  // wall ghosting (X-ray) reflects view mode
  useEffect(() => {
    game.current.wallOpacity = viewMode === "third" ? (xray ? 0.12 : 0.4) : 0.5;
  }, [viewMode, xray, game]);

  // stable callbacks on the game object
  useEffect(() => {
    game.current.onAligned = () => {
      if (game.current.phase === "flashlight") advanceTo("wristlock");
    };
    game.current.onPullComplete = () => {
      audioRef.current.alignChime();
      triggerHaptic(true);
      advanceTo("stairwell");
    };
    game.current.onDescentComplete = () => advanceTo("exit");
    game.current.onHazard = () => {
      staminaRef.current = Math.max(0, staminaRef.current - 13);
      audioRef.current.hazardZap();
      setRedFlash(true);
      setTimeout(() => setRedFlash(false), 320);
      triggerHaptic(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- dialogue loader (handles observer merge) --------
  const loadDialogue = useCallback(async (displayPov, ph) => {
    let data;
    if (displayPov === "observer") {
      const [a, b] = await Promise.all([fetchDialogue("stanley", ph), fetchDialogue("brian", ph)]);
      const map = new Map();
      [...a, ...b].forEach((d) => map.set(d.order, d));
      data = Array.from(map.values()).sort((x, y) => x.order - y.order);
    } else {
      data = await fetchDialogue(displayPov, ph);
    }
    setLines(data);
    setDialogIndex(0);
    setDialogueDone(data.length === 0);
  }, []);

  // -------- phase transition --------
  const advanceTo = useCallback(
    (nextPhase) => {
      const m = SCENE_MODE[nextPhase];
      game.current.enterPhase(nextPhase, interactionPov);
      game.current.mode = m;
      game.current.impactT = 0;
      game.current.camOffX = 0; game.current.camOffZ = 0; // recenter the D-pad pan each phase
      setPhase(nextPhase);
      setMode(m);
      setEmitCount(0);
      setPullPct(0);
      setAligned(false);
      setAimNorm(0.1);
      loadDialogue(pov, nextPhase);
      if (nextPhase === "acoustic" && !studyRef.current) runningRef.current = true;
      if (nextPhase === "exit") {
        game.current.roar = 0;
        audioRef.current.startRoar();
        const s = sirensRef.current;
        if (s) {
          try {
            s.currentTime = 0;
            s.volume = 0;
            s.muted = audioRef.current.isMuted;
          } catch (e) {}
          const p = s.play();
          if (p && p.catch) p.catch(() => {});
        }
      }
    },
    [game, loadDialogue, interactionPov, pov]
  );

  // -------- start --------
  const startExperience = useCallback(
    (chosen, opts = {}) => {
      const tour = !!opts.tour;
      const study = !!opts.study || tour;
      studyRef.current = study;
      setStudyMode(study);
      tourRef.current = tour;
      setTourMode(tour);
      tourStepRef.current = 0;
      setMenuOpen(false);
      manualSmokeRef.current = false;
      smokeStateRef.current = 0.7;
      setSmokeDensity(0.7);
      audioRef.current.resume();
      audioRef.current.startAmbience();
      remainingRef.current = SIM_TOTAL;
      breathRef.current = 100;
      staminaRef.current = 100;
      runningRef.current = false;
      decayFiredRef.current = false;
      endedRef.current = false;
      audioRef.current.setDecay(false);
      audioRef.current.stopRoar();
      game.current.roar = 0;
      setRemaining(SIM_TOTAL);
      setBreath(100);
      setStamina(100);
      setDecay(false);
      setDustConsume(false);
      setDescentFloor(81);
      setGate(null);
      gate44Ref.current = false;
      gate31Ref.current = false;
      brief84Ref.current = false;
      brief44Ref.current = false;
      brief31Ref.current = false;
      if (testimonyRef.current) { try { testimonyRef.current.pause(); } catch (e) {} }
      if (sirensRef.current) { try { sirensRef.current.pause(); sirensRef.current.currentTime = 0; } catch (e) {} }
      audioRef.current.duck(false);
      if (game.current) { game.current.camOffX = 0; game.current.camOffZ = 0; game.current.lookYaw = 0; game.current.lookPitch = 0; }
      const isObs = chosen === "observer";
      const iPov = chosen === "stanley" ? "stanley" : "brian";
      setPov(chosen);
      setViewMode(isObs ? "third" : tour ? "third" : "first");
      setXray(isObs);
      game.current.pov = iPov;
      game.current.wallOpacity = isObs ? 0.12 : 0.5;
      game.current.enterPhase("impact", iPov);
      game.current.mode = "prologue";
      game.current.impactT = 0;
      prologueAdvancedRef.current = false;
      observerLeadRef.current = "stanley";
      leadSrcRef.current = null;
      testimonyActiveRef.current = false;
      brief84Ref.current = false;
      brief44Ref.current = false;
      brief31Ref.current = false;
      setPhase("impact");
      setMode("prologue");
      setScreen("play");
      loadDialogue(chosen, "impact");
    },
    [game, loadDialogue]
  );

  // Play a real testimony clip as a background layer — never pauses the sim/timer,
  // and ducks the ambient chaos by 40% while the survivor's voice is speaking.
  const playTestimony = useCallback((url) => {
    const a = testimonyRef.current;
    if (!a) return;
    try {
      a.pause();
      a.src = url;
      a.currentTime = 0;
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
      testimonyActiveRef.current = true;
      if (ttsRef.current) ttsRef.current.pause();
      a.muted = audioRef.current.isMuted;
      a.volume = Math.min(1, (audioRef.current.volume || 0.9) + 0.1);
      audioRef.current.duck(true);
      a.onended = () => { testimonyActiveRef.current = false; audioRef.current.duck(false); setNowPlaying((n) => (n && n.src === url ? null : n)); };
      activeAudioRef.current = a;
      const who = url.includes("stanley") ? "Stanley" : "Brian";
      const floor = url.includes("_84") ? "84th floor" : url.includes("_44") ? "44th floor" : url.includes("_31") ? "31st floor" : "";
      setNowPlaying({ kind: "testimony", label: `Survivor testimony · ${who}${floor ? " — " + floor : ""}`, caption: `${who === "Stanley" ? "Stanley Praimnath" : "Brian Clark"}${floor ? " · " + floor : ""} testimony`, src: url });
      const p = a.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {}
  }, []);

  const stopTestimony = useCallback(() => {
    const a = testimonyRef.current;
    if (a) { try { a.pause(); a.onended = null; } catch (e) {} }
    testimonyActiveRef.current = false;
    if (audioRef.current) audioRef.current.duck(false);
  }, []);

  // HUD Skip / Replay for the currently-playing testimony clip
  const replayTestimony = useCallback(() => {
    const el = activeAudioRef.current;
    if (el) { try { el.currentTime = 0; const p = el.play(); if (p && p.catch) p.catch(() => {}); } catch (e) {} }
  }, []);
  const skipTestimony = useCallback(() => {
    const el = activeAudioRef.current;
    if (el) { try { el.pause(); el.ontimeupdate = null; el.onended = null; } catch (e) {} }
    audioRef.current.duck(false);
    // skipping the prologue lead jumps straight to the strike + next phase
    if (nowPlaying && nowPlaying.kind === "lead" && game.current.phase === "impact" && !prologueAdvancedRef.current) {
      prologueAdvancedRef.current = true;
      game.current.impactT = 1;
      audioRef.current.impactBoom();
      setTimeout(() => audioRef.current.impactBoom(), 400);
      triggerHaptic(true);
      setTimeout(() => advanceTo("acoustic"), 900);
    }
    setNowPlaying(null);
  }, [nowPlaying, advanceTo, triggerHaptic]);

  const endRun = useCallback(
    (win, reason) => {
      if (endedRef.current) return;
      endedRef.current = true;
      runningRef.current = false;
      audioRef.current.stopAmbience();
      audioRef.current.stopRoar();
      if (sirensRef.current) { try { sirensRef.current.pause(); } catch (e) {} }
      if (ttsRef.current) ttsRef.current.pause();
      if (witnessRef.current) witnessRef.current.pause();
      leadSrcRef.current = null;
      testimonyActiveRef.current = false;
      if (testimonyRef.current) { try { testimonyRef.current.pause(); } catch (e) {} }
      audioRef.current.duck(false);
      postProgress(game.current.pov, game.current.phase, remainingRef.current);
      if (win) { setDustConsume(false); setScreen("legacy"); }
      else {
        setFailReason(reason);
        if (reason === "collapse") triggerCollapse(reason);
        else setScreen("fail");
      }
    },
    [game, triggerCollapse]
  );

  const onGuidedStudy = useCallback(
    (povId) => { startExperience(povId, { study: true }); },
    [startExperience]
  );

  const onGuidedTour = useCallback(
    () => { startExperience("brian", { tour: true }); },
    [startExperience]
  );

  const handleStartRequest = useCallback(
    (chosen) => {
      if (chosen === "observer") startExperience(chosen);
      else setPendingStart(chosen);
    },
    [startExperience]
  );


  const confirmPreLaunch = useCallback(() => {
    const chosen = pendingStart;
    setPendingStart(null);
    if (chosen) startExperience(chosen);
  }, [pendingStart, startExperience]);

  const closeGate = useCallback(() => {
    // the 44th-floor exchange was brief; the 31st-floor conference-room call was ~4 min
    const cost = gateRef.current === "call31" ? 240 : 60;
    remainingRef.current = Math.max(0, remainingRef.current - cost);
    setRemaining(remainingRef.current);
    setGate(null);
    if (!endedRef.current) runningRef.current = true;
  }, []);

  const jumpToChapter = useCallback(
    (key) => {
      const g = game.current;
      if (key === "impact") {
        gate44Ref.current = false; gate31Ref.current = false; setGate(null); advanceTo("impact");
      } else if (key === "rescue") {
        gate44Ref.current = false; gate31Ref.current = false; setGate(null); advanceTo("acoustic");
      } else if (key === "sky44") {
        advanceTo("descent"); g.descentFloor = 45; setDescentFloor(45);
        gate44Ref.current = true; gate31Ref.current = false; gateRef.current = "sky44"; setGate("sky44");
      } else if (key === "call31") {
        advanceTo("descent"); g.descentFloor = 32; setDescentFloor(32);
        gate44Ref.current = true; gate31Ref.current = true; gateRef.current = "call31"; setGate("call31");
      } else if (key === "street") {
        setGate(null); advanceTo("exit");
      }
    },
    [advanceTo, game]
  );

  // keep the live smoke-density ref in sync with the dial
  useEffect(() => { game.current.smokeDensity = smokeDensity; }, [smokeDensity, game]);

  // load available speech voices for the two survivors
  useEffect(() => {
    const load = () => { try { voicesRef.current = window.speechSynthesis.getVoices() || []; } catch (e) {} };
    load();
    try { window.speechSynthesis.addEventListener("voiceschanged", load); } catch (e) {}
    return () => { try { window.speechSynthesis.removeEventListener("voiceschanged", load); } catch (e) {} };
  }, []);

  // -------- Guided Tour driver: hands-free, narration-paced beat sequence --------
  // impact → rescue (Bible/acoustic) → 44th guard → 31st 911 calls → street exit → Legacy
  useEffect(() => {
    if (!tourMode || screen !== "play") return;
    const id = setInterval(() => {
      if (gateRef.current) return;          // a gate is open — it auto-advances itself
      if (!dialogueDoneRef.current) return; // narration still playing for this beat
      const step = tourStepRef.current;
      if (step === 0) { tourStepRef.current = 1; jumpToChapter("rescue"); }
      else if (step === 1) { tourStepRef.current = 2; jumpToChapter("sky44"); }
      else if (step === 2) { tourStepRef.current = 3; jumpToChapter("call31"); }
      else if (step === 3) { tourStepRef.current = 4; jumpToChapter("street"); }
      else if (step === 4) {
        tourStepRef.current = 5;
        game.current.roar = 1;
        audioRef.current.setRoar(1);
        audioRef.current.collapseRumble();
        setDustConsume(true);
        setTimeout(() => endRun(true), 2600);
        clearInterval(id);
      }
    }, 1500);
    return () => clearInterval(id);
  }, [tourMode, screen, jumpToChapter, endRun, game]);

  const currentChapter = gate === "sky44" ? "sky44" : gate === "call31" ? "call31"
    : phase === "impact" ? "impact"
    : (phase === "acoustic" || phase === "flashlight" || phase === "wristlock") ? "rescue"
    : phase === "exit" ? "street" : "";

  // -------- witness lead-in audio per scene --------
  useEffect(() => {
    if (screen !== "play") return;
    const w = witnessRef.current;
    if (!w) return;
    let src = WITNESS[phase];
    const isPrologue = phase === "impact";
    const isObserver = pov === "observer";
    const stanleyLead = isPrologue && pov !== "brian"; // Stanley + Observer share the lead clip
    if (stanleyLead) src = "/audio/testimony/stanley_lead.mp3";
    // Same clip already loaded & playing (e.g. a StrictMode remount / unrelated re-render)?
    // Leave it running untouched so it is never clipped at the very beginning.
    if (src && leadSrcRef.current === src && !w.paused && !w.ended) {
      return;
    }
    w.ontimeupdate = null;
    if (src) {
      leadSrcRef.current = src;
      // The imported testimony must ALWAYS play first — silence any queued spoken prompts.
      testimonyActiveRef.current = true;
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
      w.src = src;
      w.volume = isPrologue ? 1.0 : 0.62; // lead plays at 100%
      w.muted = muted;
      activeAudioRef.current = w;
      const label = stanleyLead
        ? "Survivor testimony · Stanley — 81st floor"
        : isPrologue
        ? "Survivor testimony · Brian — 84th floor"
        : "Survivor testimony";
      const caption = stanleyLead
        ? "Stanley Praimnath · 81st-floor eyewitness testimony"
        : isPrologue
        ? "Brian Clark · 84th-floor eyewitness testimony"
        : "Eyewitness testimony";
      setNowPlaying({ kind: "lead", label, caption, src });
      const doStrike = () => {
        if (prologueAdvancedRef.current) return;
        prologueAdvancedRef.current = true;
        if (leadTimerRef.current) { clearTimeout(leadTimerRef.current); leadTimerRef.current = null; }
        testimonyActiveRef.current = false;
        game.current.impactT = 1;
        audioRef.current.impactBoom();
        setTimeout(() => audioRef.current.impactBoom(), 520);
        audioRef.current.duck(false);
        triggerHaptic(true);
        setNowPlaying(null);
        setTimeout(() => advanceTo("acoustic"), 1000);
      };
      if (stanleyLead) {
        // office ambience (phones/chatter) drops to 10% until the impact Boom
        audioRef.current.duck(true, 0.1);
        observerLeadRef.current = "stanley";
        w.ontimeupdate = () => {
          const dur = w.duration || 19;
          const frac = Math.min(1, w.currentTime / dur);
          if (isObserver) {
            // Stanley's clip drives the plane halfway in; Brian's clip finishes the approach
            game.current.impactT = observerLeadRef.current === "brian"
              ? Math.min(0.999, 0.5 + frac * 0.5)
              : Math.min(0.5, frac * 0.5);
          } else {
            // plane approaches across the FULL clip and only meets the window as it ends
            game.current.impactT = Math.min(0.999, frac);
          }
        };
        // fallback strike if an 'ended' event is missed — measured from the CLIP's real
        // duration (not the effect run) so the natural onended always drives the transition.
        const armFallback = () => {
          if (leadTimerRef.current) clearTimeout(leadTimerRef.current);
          const dur = isFinite(w.duration) && w.duration > 0 ? w.duration : 0;
          const ms = isObserver ? 75000 : (dur > 0 ? dur * 1000 + 3000 : 25000);
          leadTimerRef.current = setTimeout(doStrike, ms);
        };
        w.onloadedmetadata = () => armFallback();
        armFallback();
      }
      w.onended = () => {
        // Observer: after Stanley's lead, chain Brian's lead on the same element before striking
        if (isObserver && observerLeadRef.current === "stanley") {
          observerLeadRef.current = "brian";
          const bsrc = "/audio/brian_impact.mp3";
          leadSrcRef.current = bsrc;
          testimonyActiveRef.current = true;
          try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
          setNowPlaying({ kind: "lead", label: "Survivor testimony · Brian — 84th floor", caption: "Brian Clark · 84th-floor eyewitness testimony", src: bsrc });
          try { w.src = bsrc; w.currentTime = 0; w.muted = muted; w.load(); } catch (e) {}
          w.play().catch(() => {});
          return;
        }
        testimonyActiveRef.current = false;
        setNowPlaying((n) => (n && n.src === src ? null : n));
        // Double Boom + plane strike fire ONLY after the entire lead concludes
        if (stanleyLead) doStrike();
      };
      w.play().catch(() => {});
    } else {
      leadSrcRef.current = null;
      testimonyActiveRef.current = false;
      w.pause();
      setNowPlaying(null);
    }
    // Cleanup only clears the fallback timer — it does NOT pause the clip, so a
    // StrictMode remount (or benign re-render) can't chop the audio at its start.
    // A genuine phase change swaps w.src above, which stops the previous clip.
    return () => { if (leadTimerRef.current) { clearTimeout(leadTimerRef.current); leadTimerRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, screen, pov]);

  // -------- per-line TTS for non-witness phases --------
  useEffect(() => {
    if (screen !== "play") return;
    const t = ttsRef.current;
    if (!t) return;
    const line = lines[dialogIndex];
    const isWitnessPhase = !!WITNESS[phase];
    if (!line || isWitnessPhase) { t.pause(); return; }
    // never start a narrator line over a recorded testimony clip
    if (testimonyActiveRef.current) { t.pause(); return; }
    t.src = `${API}/narrative/tts/${line.order}.mp3`;
    t.muted = muted;
    t.onended = () => {
      setDialogIndex((i) => {
        if (i < lines.length - 1) return i + 1;
        setDialogueDone(true);
        return i;
      });
    };
    t.play().catch(() => {});
    return () => { if (t) { t.onended = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogIndex, lines, phase, screen]);

  // -------- expose the active dialogue line's order to the 3D scene --------
  useEffect(() => {
    game.current.lineOrder = (lines[dialogIndex] && lines[dialogIndex].order) || null;
  }, [dialogIndex, lines, game]);

  // -------- auto-advance subtitles during witness phases --------
  useEffect(() => {
    if (screen !== "play" || !WITNESS[phase] || lines.length === 0) return;
    const id = setInterval(() => {
      setDialogIndex((i) => {
        if (i < lines.length - 1) return i + 1;
        setDialogueDone(true);
        clearInterval(id);
        return i;
      });
    }, 5200);
    return () => clearInterval(id);
  }, [screen, phase, lines.length]);

  // -------- main frame loop --------
  useEffect(() => {
    if (screen !== "play") return;
    lastRef.current = performance.now();
    const tick = () => {
      const now = performance.now();
      let dt = (now - lastRef.current) / 1000;
      lastRef.current = now;
      if (dt > 0.1) dt = 0.1;
      const g = game.current;

      // unified movement input (keyboard + button + D-Pad + later hand-tracking)
      const d = g.dpad || {};
      // D-pad free locomotion: X strafe + Z forward/back (no inversion).
      // Left/right disabled during flashlight aiming; down descends during the stairwell.
      const inFlash = g.phase === "flashlight";
      const strafe = inFlash ? 0 : ((d.right ? 1 : 0) - (d.left ? 1 : 0));
      let fb = 0;
      if (g.phase === "descent") fb = d.up ? 1 : 0; // up = step back (down descends/forward)
      else if (!inFlash) fb = (d.up ? 1 : 0) - (d.down ? 1 : 0);
      const moveYaw = g.orbitOn ? (g.orbitYaw || 0) : (g.lookYaw || 0);
      g.camOffX = Math.max(-2.6, Math.min(2.6, (g.camOffX || 0) + (strafe * Math.cos(moveYaw) + fb * Math.sin(moveYaw)) * dt * 2.4));
      g.camOffZ = Math.max(-2.0, Math.min(2.6, (g.camOffZ || 0) + (-fb * Math.cos(moveYaw) + strafe * Math.sin(moveYaw)) * dt * 2.0));
      if (g.phase === "descent") {
        g.descentActive = !gateRef.current && (keyHeldRef.current || btnHoldRef.current || d.down || !!g.xrDescend) && dialogueDoneRef.current;
        g.running = g.descentActive && (d.run || shiftHeldRef.current);
      } else {
        g.running = false;
        if (g.phase === "flashlight" && dialogueDoneRef.current) {
          if (d.left) { g.rotateFlash(-dt * 1.6); setAimNorm((n) => Math.max(0, n - dt * 0.5)); }
          if (d.right) { g.rotateFlash(dt * 1.6); setAimNorm((n) => Math.min(1, n + dt * 0.5)); }
        }
      }
      if (g.phase === "exit") {
        g.roar = Math.min(1, (g.roar || 0) + dt / 12);
        // duck the collapse roar while a survivor's testimony is speaking
        audioRef.current.setRoar(g.roar * (testimonyActiveRef.current ? 0.45 : 1));
        // emergency sirens fade in as the street chaos intensifies
        if (sirensRef.current) {
          sirensRef.current.volume = Math.min(0.85, g.roar * 0.95) * (audioRef.current.volume || 0.9);
        }
      }
      // Global evacuation state — every NPC + both survivors break into a sprint
      // for the entire final "street" chapter, and (in a timed run) once the
      // historical clock passes 9:55 AM (remaining <= 239s of the 56-min window).
      g.evac = g.phase === "exit" || (!studyRef.current && remainingRef.current > 0 && remainingRef.current <= 239);

      if (runningRef.current) {
        remainingRef.current -= dt * SIM_RATE;
        if (remainingRef.current <= 0) { remainingRef.current = 0; endRun(false, "collapse"); }
        if (remainingRef.current <= DECAY_THRESHOLD && !decayFiredRef.current) {
          decayFiredRef.current = true;
          audioRef.current.setDecay(true);
          g.smoke = 0.16;
        }
      }

      const inDescent = g.phase === "descent";
      if (inDescent) {
        // real testimony clips — fire once at their floors, in the background (non-blocking)
        if (!brief84Ref.current) { brief84Ref.current = true; playTestimony("/audio/testimony/brian_84.mp3"); }
        else if (!brief44Ref.current && g.descentFloor <= 44) { brief44Ref.current = true; playTestimony("/audio/testimony/stanley_44.mp3"); }
        else if (!brief31Ref.current && g.descentFloor <= 40) { brief31Ref.current = true; playTestimony("/audio/testimony/brian_31.mp3"); }
      }
      if (inDescent) {
        if (!gate44Ref.current && g.descentFloor <= 44) {
          gate44Ref.current = true;
          runningRef.current = false;
          g.descentActive = false; keyHeldRef.current = false; btnHoldRef.current = false;
          setGate("sky44");
        } else if (gate44Ref.current && !gate31Ref.current && g.descentFloor <= 31) {
          gate31Ref.current = true;
          runningRef.current = false;
          g.descentActive = false; keyHeldRef.current = false; btnHoldRef.current = false;
          setGate("call31");
        }
      }
      if (inDescent && g.descentActive && !studyRef.current) {
        const rate = (decayFiredRef.current ? 7.5 : 5.0) * (g.running ? 2.1 : 1);
        breathRef.current = Math.max(0, breathRef.current - dt * rate);
        if (g.running) staminaRef.current = Math.max(0, staminaRef.current - dt * 7);
      } else {
        breathRef.current = Math.min(100, breathRef.current + dt * 6);
      }
      staminaRef.current = Math.min(100, staminaRef.current + dt * 4);
      if (!endedRef.current && !studyRef.current) {
        if (breathRef.current <= 0) endRun(false, "breath");
        else if (staminaRef.current <= 0) endRun(false, "stamina");
      }

      // ---- automated respiratory-distress coughing in smoke-filled zones ----
      const smokeZone = g.phase === "acoustic" || g.phase === "flashlight" || g.phase === "wristlock" || g.phase === "descent";
      // extra thick air in the lower stairwell / during structural decay
      const heavy = (g.phase === "descent" && (decayFiredRef.current || g.descentFloor < 40));
      if (smokeZone && !gateRef.current) {
        coughElapsedRef.current += dt;
        if (coughElapsedRef.current >= coughNextRef.current) {
          coughElapsedRef.current = 0;
          coughNextRef.current = (heavy ? 3.2 : 5.5) + Math.random() * 2.5;
          coughLevelRef.current = 1;
          // duck the cough while verbatim testimony is still being delivered
          audioRef.current.cough(!dialogueDoneRef.current);
          triggerHaptic(false);
          // coughing costs a little extra breath in the timed run
          if (!studyRef.current) breathRef.current = Math.max(0, breathRef.current - (heavy ? 6 : 3));
        }
      } else {
        coughElapsedRef.current = 0;
      }
      coughLevelRef.current = Math.max(0, coughLevelRef.current - dt * 2.2);
      g.coughLevel = coughLevelRef.current;

      mirrorRef.current += dt;
      if (mirrorRef.current > 0.08) {
        mirrorRef.current = 0;
        setRemaining(remainingRef.current);
        setBreath(breathRef.current);
        setStamina(staminaRef.current);
        setDecay(decayFiredRef.current);
        setAligned(g.aligned);
        setDescentFloor(g.descentFloor);
        setAshWash(g.phase === "exit" ? Math.min(0.62, (g.roar || 0) * 0.72) : 0);
        // Adaptive Smoke Engine — density scales with the floor unless manually overridden
        if (!manualSmokeRef.current) {
          const ph = g.phase;
          let d = 0.7;
          if (ph === "impact") d = 0.98;
          else if (ph === "descent") { const f = g.descentFloor; d = f >= 80 ? 0.7 : f >= 45 ? 0.55 : 0.65; }
          else if (ph === "exit") d = 0.9;
          g.smokeDensity = d;
          if (Math.abs(d - smokeStateRef.current) > 0.02) { smokeStateRef.current = d; setSmokeDensity(d); }
        }
        g.hud = {
          countdown: formatSim(remainingRef.current),
          clock: "",
          breath: breathRef.current,
          stamina: staminaRef.current,
          decay: decayFiredRef.current,
          line: linesRef.current[dialogIndexRef.current] || null,
        };
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [screen, game, endRun]);

  // keyboard descent
  useEffect(() => {
    if (screen !== "play" || phase !== "descent") return;
    const down = (e) => {
      if (e.key === "w" || e.key === "W" || e.key === "ArrowDown") keyHeldRef.current = true;
      if (e.key === "Shift") shiftHeldRef.current = true;
    };
    const up = (e) => {
      if (e.key === "w" || e.key === "W" || e.key === "ArrowDown") keyHeldRef.current = false;
      if (e.key === "Shift") shiftHeldRef.current = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      keyHeldRef.current = false;
      game.current.descentActive = false;
    };
  }, [screen, phase, dialogueDone, game]);

  // keyboard D-pad mirror — Arrow keys / WASD / Numpad 8-4-6-2 drive the same dpad dirs
  useEffect(() => {
    if (screen !== "play") return;
    const MAP = {
      ArrowUp: "up", KeyW: "up", Numpad8: "up",
      ArrowDown: "down", KeyS: "down", Numpad2: "down",
      ArrowLeft: "left", KeyA: "left", Numpad4: "left",
      ArrowRight: "right", KeyD: "right", Numpad6: "right",
    };
    const held = new Set();
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable || (el.getAttribute && el.getAttribute("type") === "range");
    };
    const setDir = (dir, val) => { game.current.dpad = game.current.dpad || {}; game.current.dpad[dir] = val; };
    const down = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return; // leave Cmd+W, Ctrl+S, etc. alone
      if (isTyping()) return;
      const dir = MAP[e.code];
      if (!dir) return;
      if (e.code === "KeyW" && game.current.phase === "descent") return; // W keeps 'descend' in the stairwell
      held.add(e.code);
      setDir(dir, true);
    };
    const up = (e) => {
      const dir = MAP[e.code];
      if (!dir) return;
      held.delete(e.code);
      const stillHeld = Array.from(held).some((c) => MAP[c] === dir);
      if (!stillHeld) setDir(dir, false);
    };
    const clearAll = () => { held.forEach((c) => setDir(MAP[c], false)); held.clear(); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clearAll);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clearAll);
      clearAll();
    };
  }, [screen, game]);


  // mute propagation
  useEffect(() => {
    audioRef.current.setMuted(muted);
    if (ttsRef.current) ttsRef.current.muted = muted;
    if (witnessRef.current) witnessRef.current.muted = muted;
    if (testimonyRef.current) testimonyRef.current.muted = muted;
  }, [muted]);

  // volume propagation (ambient chaos) + persistence
  useEffect(() => {
    audioRef.current.setVolume(volume);
    try { localStorage.setItem("tab_volume", String(volume)); } catch (e) {}
  }, [volume]);

  // -------- interaction handlers --------
  const handlers = useMemo(
    () => ({
      onImpact: () => {
        // spatialized "Double Boom" — two concussions, second is the strike
        prologueAdvancedRef.current = true;
        if (leadTimerRef.current) { clearTimeout(leadTimerRef.current); leadTimerRef.current = null; }
        if (witnessRef.current) witnessRef.current.ontimeupdate = null; // stop audio-driven plane sync
        audioRef.current.duck(false);
        audioRef.current.impactBoom();
        triggerHaptic(true);
        setTimeout(() => { audioRef.current.impactBoom(); triggerHaptic(true); }, 560);
        const useSlowmo = slowmoRef.current && game.current.pov !== "brian";
        setCinematic(useSlowmo);
        cancelAnimationFrame(impactRafRef.current);
        let last = performance.now();
        const step = () => {
          const now = performance.now();
          const dt = (now - last) / 1000;
          last = now;
          const t = game.current.impactT;
          // cinematic slow-motion beat as Flight 175 banks into the window
          let rate = 1 / 1.4;
          if (useSlowmo && t > 0.6 && t < 0.86) rate = 1 / 4.6;
          game.current.impactT = Math.min(1, t + dt * rate);
          if (game.current.impactT < 1) impactRafRef.current = requestAnimationFrame(step);
          else {
            setCinematic(false);
            const w = witnessRef.current;
            const stillPlaying = w && !w.paused && !w.ended && (!isFinite(w.duration) || w.currentTime < w.duration - 0.15);
            if (stillPlaying) {
              // The on-screen hint promises the testimony keeps playing — so let Stanley's
              // lead finish naturally, THEN swap the scene audio, instead of cutting it off.
              const finish = () => {
                w.onended = null;
                if (leadTimerRef.current) { clearTimeout(leadTimerRef.current); leadTimerRef.current = null; }
                advanceTo("acoustic");
              };
              w.onended = finish;
              const rem = isFinite(w.duration) ? Math.max(0, w.duration - w.currentTime) : 6;
              if (leadTimerRef.current) clearTimeout(leadTimerRef.current);
              leadTimerRef.current = setTimeout(finish, rem * 1000 + 1500);
            } else {
              advanceTo("acoustic");
            }
          }
        };
        impactRafRef.current = requestAnimationFrame(step);
      },
      onEmit: () => {
        audioRef.current.ring();
        game.current.emitPulse();
        setEmitCount((c) => {
          const n = c + 1;
          if (n >= game.current.emitsNeeded) setTimeout(() => advanceTo("flashlight"), 500);
          return n;
        });
      },
      onTriangulate: () => advanceTo("flashlight"),
      onLock: () => { audioRef.current.resume(); game.current.autoAlign(); },
      onAim: (n) => { setAimNorm(n); game.current.setSweepNorm(n); },
      onPullStart: () => {
        audioRef.current.resume();
        cancelAnimationFrame(pullRafRef.current);
        let last = performance.now();
        const step = () => {
          const now = performance.now();
          const dt = (now - last) / 1000;
          last = now;
          const v = Math.min(1, game.current.pullProgress + dt * 0.6);
          game.current.setPull(v);
          setPullPct(v);
          if (v < 1) pullRafRef.current = requestAnimationFrame(step);
        };
        pullRafRef.current = requestAnimationFrame(step);
      },
      onPullEnd: () => {
        cancelAnimationFrame(pullRafRef.current);
        if (game.current.pullProgress < 1) {
          let last = performance.now();
          const step = () => {
            const now = performance.now();
            const dt = (now - last) / 1000;
            last = now;
            const v = Math.max(0, game.current.pullProgress - dt * 0.5);
            game.current.setPull(v);
            setPullPct(v);
            if (v > 0) pullRafRef.current = requestAnimationFrame(step);
          };
          pullRafRef.current = requestAnimationFrame(step);
        }
      },
      onChooseStairwell: () => advanceTo("descent"),
      onDescendStart: () => { btnHoldRef.current = true; },
      onDescendEnd: () => { btnHoldRef.current = false; },
      onExit: () => {
        // final run: collapse roar peaks, dust cloud rolls up Liberty St,
        // screen is consumed by dust, then fades to the commemorative ribbon.
        game.current.roar = 1;
        audioRef.current.setRoar(1);
        audioRef.current.collapseRumble();
        triggerHaptic(true);
        setDustConsume(true);
        setTimeout(() => endRun(true), 2600);
      },
    }),
    [advanceTo, endRun, game, dialogueDone, triggerHaptic]
  );

  const onNextLine = useCallback(() => {
    setDialogIndex((i) => {
      if (i < lines.length - 1) return i + 1;
      setDialogueDone(true);
      return i;
    });
  }, [lines.length]);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);
  const enterVR = useCallback(() => { audioRef.current.resume(); try { xrStore.enterVR(); } catch (e) {} }, [xrStore]);
  // Landing "Enter Immersive VR": on headset devices mount the scene then request the
  // XR session; on desktop/mobile drop straight into the spatial (Observer) experience.
  const enterVRFromLanding = useCallback((chosen) => {
    audioRef.current.resume();
    const povId = chosen || "observer";
    startExperience(povId);
    if (vrSupported) setTimeout(() => { try { xrStore.enterVR(); } catch (e) {} }, 650);
  }, [vrSupported, startExperience, xrStore]);
  const replay = useCallback(() => { endedRef.current = false; setDustConsume(false); setCollapsing(false); setTourMode(false); tourRef.current = false; setMenuOpen(false); setScreen("menu"); }, []);

  // -------- central user menu actions --------
  const goHome = useCallback(() => {
    endedRef.current = true;
    setMenuOpen(false); setDustConsume(false); setCollapsing(false);
    setTourMode(false); tourRef.current = false;
    audioRef.current.stopAmbience(); audioRef.current.stopRoar();
    remainingRef.current = SIM_TOTAL; setRemaining(SIM_TOTAL);
    setScreen("menu");
  }, []);
  const restartScene = useCallback(() => {
    setMenuOpen(false);
    advanceTo(game.current.phase || "impact");
  }, [advanceTo, game]);
  const exitToHome = useCallback(() => {
    endedRef.current = true;
    setMenuOpen(false); setDustConsume(false); setCollapsing(false);
    setTourMode(false); tourRef.current = false;
    audioRef.current.stopAmbience(); audioRef.current.stopRoar();
    remainingRef.current = SIM_TOTAL; setRemaining(SIM_TOTAL);
    setScreen("landing");
  }, []);
  const toggleView = useCallback((v) => setViewMode(v), []);
  const toggleXray = useCallback(() => setXray((x) => !x), []);
  const onDir = useCallback((dir, pressed) => {
    game.current.dpad = { ...game.current.dpad, [dir]: pressed };
  }, [game]);

  const reunited = PHASES.indexOf(phase) >= PHASES.indexOf("wristlock");
  const currentLine = lines[dialogIndex] || null;
  const showFlag = screen === "landing" || screen === "menu";

  return (
    <div className={collapsing ? "collapse-shake" : ""} style={{ position: "fixed", inset: 0, background: "#07080a", overflow: "hidden" }} data-testid="app-root">
      {showFlag && (
        <>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "url(/brand/american_flag.jpg)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.6, zIndex: 1 }} />
          <div style={{ position: "absolute", inset: 0, background: "rgba(7,10,20,0.35)", zIndex: 1 }} />
        </>
      )}

      {screen === "play" && (
        <div style={{ position: "absolute", inset: 0, zIndex: 2 }} className="tab-vignette">
          <SceneCanvas game={game} audio={audioRef.current} mode={mode} viewMode={viewMode} pov={interactionPov} xrStore={xrStore} gate={gate} tour={tourMode} />
        </div>
      )}

      {redFlash && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(185,56,38,0.5)", zIndex: 30, pointerEvents: "none", animation: "red-flash 0.32s ease" }} />
      )}

      {/* Ash-choked colour grade — the street fades to a grey, ash-coated wash as the dust wall passes */}
      {ashWash > 0.01 && (
        <div data-testid="ash-wash-overlay" style={{ position: "absolute", inset: 0, zIndex: 24, pointerEvents: "none", background: "rgba(198,193,182,1)", opacity: ashWash, mixBlendMode: "screen", transition: "opacity 0.2s linear" }} />
      )}

      {dustConsume && (
        <div data-testid="dust-consume-overlay" style={{ position: "absolute", inset: 0, zIndex: 55, pointerEvents: "none", animation: "dust-consume 2.6s ease-in forwards", background: "radial-gradient(circle at 50% 60%, rgba(200,196,186,0.9), rgba(150,146,138,0.95) 45%, rgba(20,20,20,0.98) 100%)" }} />
      )}

      {collapsing && (
        <div data-testid="collapse-overlay" style={{ position: "absolute", inset: 0, zIndex: 58, pointerEvents: "none", animation: "charcoal-consume 2.3s ease-in forwards", background: "radial-gradient(circle at 50% 45%, rgba(120,116,110,0.85), rgba(40,40,42,0.95) 40%, rgba(18,18,20,1) 100%)" }} />
      )}

      {/* cinematic letterbox during the slow-mo impact beat */}
      {screen === "play" && (
        <>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: cinematic ? "12vh" : 0, background: "#000", zIndex: 32, pointerEvents: "none", transition: "height 0.5s ease" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: cinematic ? "12vh" : 0, background: "#000", zIndex: 32, pointerEvents: "none", transition: "height 0.5s ease" }} />
        </>
      )}

      {(screen === "landing" || screen === "menu") && (
        <>
          <LandingCover visible={screen === "landing"} />
          <POVPicker visible={screen === "menu"} povs={povs} onStart={handleStartRequest} onGuidedStudy={onGuidedStudy} onGuidedTour={onGuidedTour} onEnterVR={enterVRFromLanding} vrSupported={vrSupported} />
          <LandingAudioControl
            muted={muted}
            volume={volume}
            onToggleMute={toggleMute}
            onVolume={(v) => { audioRef.current.resume(); setVolume(v); if (muted && v > 0) setMuted(false); }}
          />
        </>
      )}

      <PreLaunchPanel
        open={!!pendingStart}
        povName={pendingStart === "stanley" ? "Stanley Praimnath" : "Brian Clark"}
        onConfirm={confirmPreLaunch}
        onCancel={() => setPendingStart(null)}
      />

      {screen === "menu" && (
        <img src="/brand/tab_seal.png" alt="TAB" data-testid="tab-seal-corner" style={{ position: "absolute", top: 16, left: 18, width: 48, height: 48, borderRadius: 999, zIndex: 16, boxShadow: "0 0 0 1px rgba(201,162,75,0.4)", display: "none" }} />
      )}

      {screen === "play" && (
        <div className="hud-grid" style={{ position: "absolute", inset: 0, zIndex: 25, pointerEvents: "none" }}>
          <div className="hud-region-top" style={{ pointerEvents: "auto" }}>
            <SurvivalHUD
              countdownText={formatSim(remaining)}
              historicalText={formatClock(IMPACT_SEC_OF_DAY + (SIM_TOTAL - remaining))}
              decay={decay}
              muted={muted}
              onToggleMute={toggleMute}
              onFactCheck={() => setFactOpen(true)}
              onEnterVR={enterVR}
              vrSupported={vrSupported}
              phaseTitle={PHASE_TASKS[phase] ? PHASE_TASKS[phase].title : ""}
              studyMode={studyMode}
              volume={volume}
              onVolume={(v) => { setVolume(v); if (muted && v > 0) setMuted(false); }}
              onMenu={() => setMenuOpen((o) => !o)}
            />
          </div>

          {/* Heading compass — top-center, reads live facing from the active camera */}
          <div
            data-testid="hud-compass-wrap"
            className="hud-compass-wrap"
            style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 27, pointerEvents: "none" }}
          >
            <Compass getHeading={() => game.current.viewHeading} />
          </div>

          {/* Skip / Replay control for the currently-playing survivor testimony */}
          {nowPlaying && (
            <div
              data-testid="testimony-control"
              className="testimony-control-pill"
              style={{ pointerEvents: "auto", display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", borderRadius: 999, background: "rgba(10,12,16,0.74)", backdropFilter: "blur(12px)", border: "1px solid rgba(201,162,75,0.42)", boxShadow: "0 8px 26px rgba(0,0,0,0.5)" }}
            >
              <Volume2 size={13} color="#c9a24b" />
              <button data-testid="testimony-replay" onClick={replayTestimony} title="Replay" style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 999, cursor: "pointer", fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "#c9a24b", background: "rgba(201,162,75,0.1)", border: "1px solid rgba(201,162,75,0.32)" }}>
                <RotateCcw size={12} /> Replay
              </button>
              <button data-testid="testimony-skip" onClick={skipTestimony} title="Skip" style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 9px", borderRadius: 999, cursor: "pointer", fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: "#e9e4d8", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)" }}>
                Skip <SkipForward size={12} />
              </button>
            </div>
          )}

          {/* Accessibility captions — gold Serif, bottom-center, synced to the playing testimony */}
          {captionsOn && nowPlaying && (
            <div
              data-testid="testimony-caption"
              className="font-serif-q tab-fade"
              style={{ position: "absolute", bottom: "20%", left: "50%", transform: "translateX(-50%)", zIndex: 31, pointerEvents: "none", maxWidth: "72%", textAlign: "center", padding: "8px 18px", borderRadius: 6, background: "rgba(8,10,14,0.66)", backdropFilter: "blur(6px)", border: "1px solid rgba(201,162,75,0.35)", color: "#f2e6c8", fontSize: 17, lineHeight: 1.35, letterSpacing: "0.01em" }}
            >
              {nowPlaying.caption || nowPlaying.label}
            </div>
          )}

          {/* Central user MENU — glass panel, gold Serif */}
          {menuOpen && (
            <>
              {/* full-screen backdrop — click anywhere to close */}
              <div
                data-testid="hud-menu-backdrop"
                onClick={() => setMenuOpen(false)}
                style={{ position: "absolute", inset: 0, zIndex: 1001, pointerEvents: "auto", background: "rgba(4,6,10,0.4)", backdropFilter: "blur(1px)" }}
              />
              <div
                data-testid="hud-menu-panel"
                className="hud-panel tab-fade"
                style={{ position: "absolute", top: 132, right: 18, zIndex: 1002, pointerEvents: "auto", width: 220, padding: "14px 14px 12px", borderRadius: 12, border: "1px solid rgba(201,162,75,0.5)", background: "rgba(10,12,16,0.85)", backdropFilter: "blur(18px)", boxShadow: "0 18px 60px rgba(0,0,0,0.55)" }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span className="font-serif-q" style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "#e4be65" }}>Menu</span>
                  <button
                    data-testid="menu-close-button"
                    onClick={() => setMenuOpen(false)}
                    aria-label="Resume"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, cursor: "pointer", color: "#f2e6c8", background: "rgba(201,162,75,0.12)", border: "1px solid rgba(201,162,75,0.35)" }}
                  >
                    <X size={16} />
                  </button>
                </div>
                {[
                  { k: "home", label: "Home", tid: "menu-home-button", fn: goHome },
                  { k: "restart", label: "Restart Scene", tid: "menu-restart-button", fn: restartScene },
                  { k: "fact", label: "Fact Check", tid: "menu-factcheck-button", fn: () => { setMenuOpen(false); setFactOpen(true); } },
                  { k: "exit", label: "Exit", tid: "menu-exit-button", fn: exitToHome },
                ].map((m) => (
                  <button
                    key={m.k}
                    data-testid={m.tid}
                    onClick={m.fn}
                    className="font-serif-q"
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", marginBottom: 6, borderRadius: 8, cursor: "pointer", fontSize: 13.5, letterSpacing: "0.03em", color: "#f2e6c8", background: "rgba(201,162,75,0.08)", border: "1px solid rgba(201,162,75,0.28)" }}
                  >
                    {m.label}
                  </button>
                ))}
                {/* mobile: audio + smoke controls live inside the menu */}
                <div className="mobile-only">
                  <button
                    data-testid="menu-audio-button"
                    onClick={() => setMuted((v) => !v)}
                    className="font-serif-q"
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", marginBottom: 6, borderRadius: 8, cursor: "pointer", fontSize: 13.5, color: "#f2e6c8", background: "rgba(201,162,75,0.08)", border: "1px solid rgba(201,162,75,0.28)" }}
                  >
                    {muted ? "Audio: Off" : "Audio: On"}
                  </button>
                  <div style={{ padding: "6px 4px 2px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#c9a24b", marginBottom: 4 }}>
                      <span style={{ letterSpacing: "0.12em" }}>SMOKE</span><span>{Math.round(smokeDensity * 100)}%</span>
                    </div>
                    <input
                      data-testid="menu-smoke-slider"
                      type="range" min={0} max={100} value={Math.round(smokeDensity * 100)}
                      onChange={(e) => { manualSmokeRef.current = true; setSmokeDensity(Number(e.target.value) / 100); }}
                      style={{ width: "100%", accentColor: "#c9a24b", cursor: "pointer" }}
                    />
                  </div>
                </div>
                {/* Accessibility */}
                <div style={{ marginTop: 4, marginBottom: 6, paddingTop: 8, borderTop: "1px solid rgba(201,162,75,0.22)" }}>
                  <div className="font-serif-q" style={{ fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "#8f96a3", marginBottom: 6 }}>Accessibility</div>
                  <button
                    data-testid="captions-toggle"
                    onClick={() => setCaptionsOn((c) => !c)}
                    className="font-serif-q"
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13.5, color: "#f2e6c8", background: "rgba(201,162,75,0.08)", border: "1px solid rgba(201,162,75,0.28)" }}
                  >
                    <span>Captions</span>
                    <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: captionsOn ? "#f2c85b" : "#7e8594" }}>{captionsOn ? "On" : "Off"}</span>
                  </button>
                </div>
                <button
                  data-testid="menu-resume-button"
                  onClick={() => setMenuOpen(false)}
                  className="font-serif-q gold-btn"
                  style={{ display: "block", width: "100%", textAlign: "center", padding: "10px 14px", marginTop: 4, borderRadius: 8, cursor: "pointer", fontSize: 13, letterSpacing: "0.08em" }}
                >
                  Resume
                </button>
              </div>
            </>
          )}

          {/* ===== TOP-LEFT region: Chapters + View toggle ===== */}
          <div className="hud-region-tl" style={{ pointerEvents: "auto" }}>
            {studyMode && !tourMode && (
              <div className="hud-chapters" style={{ position: "relative" }}>
                <button
                  data-testid="study-chapters-toggle"
                  onClick={() => setChaptersOpen((o) => !o)}
                  className="ghost-btn hud-touch"
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 999, fontSize: 11, cursor: "pointer", color: chaptersOpen ? "#f2c85b" : undefined }}
                >
                  <BookOpen size={14} /> Chapters
                </button>
                {chaptersOpen && (
                  <div style={{ position: "absolute", top: 46, left: 0, zIndex: 1000 }}>
                    <StudyChapters current={currentChapter} onJump={(k) => { jumpToChapter(k); setChaptersOpen(false); }} />
                  </div>
                )}
              </div>
            )}
            <div className="hud-viewtoggle" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
              <ViewControls
                viewMode={viewMode}
                onToggleView={toggleView}
                xray={xray}
                onToggleXray={toggleXray}
                scrub={scrubFloor}
                onScrub={setScrubFloor}
                isObserver={pov === "observer"}
              />
              {phase === "impact" && pov !== "brian" && (
                <button
                  data-testid="impact-slowmo-toggle"
                  onClick={() => setSlowmo((s) => !s)}
                  className="hud-panel"
                  style={{ pointerEvents: "auto", padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: slowmo ? "#f2c85b" : "#7e8594" }}
                >
                  Slow-Mo Impact Cam · {slowmo ? "On" : "Off"}
                </button>
              )}
            </div>
          </div>

          {/* ===== TOP-RIGHT region: cutaway minimap ===== */}
          <div className="hud-region-tr" style={{ pointerEvents: "auto" }}>
            <div className="hud-minimap">
              <MinimapCutaway
                descentFloor={descentFloor}
                reunited={reunited}
                scrubFloor={viewMode === "third" ? scrubFloor : null}
              />
            </div>
          </div>

          {/* ===== BOTTOM-LEFT region: D-pad ===== */}
          {!tourMode && (
            <div className="hud-region-bl" style={{ pointerEvents: "auto" }}>
              <DPad onDir={onDir} />
            </div>
          )}

          {/* ===== BOTTOM-CENTER region: subtitle · callout · task prompt · hide-text ===== */}
          <div className="hud-region-bc">
            {phase === "descent" && screen === "play" && !descentGuidanceDismissed && (
              <div
                data-testid="descent-guidance"
                style={{ maxWidth: "100%", padding: "9px 18px", borderRadius: 999, background: "rgba(10,12,16,0.72)", backdropFilter: "blur(10px)", border: "1px solid rgba(201,162,75,0.4)", color: "#f2e6c8", fontSize: 14.5, textAlign: "center", boxShadow: "0 10px 30px rgba(0,0,0,0.5)", pointerEvents: "auto", display: "inline-flex", alignItems: "center", gap: 12 }}
              >
                Keep descending to the South Tower Lobby
                <button
                  data-testid="descent-guidance-ok"
                  onClick={() => setDescentGuidanceDismissed(true)}
                  style={{ padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", cursor: "pointer", color: "#10120f", background: "linear-gradient(180deg,#f7d477,#c9a24b)", border: "1px solid #f2c85b" }}
                >
                  Ok
                </button>
              </div>
            )}
            {currentLine && !hideText && (
              <div style={{ width: "min(780px, 100%)", pointerEvents: "auto" }}>
                <SubtitleRibbon
                  line={currentLine}
                  index={dialogIndex}
                  total={lines.length}
                  onNext={onNextLine}
                  canNext={dialogIndex < lines.length - 1}
                />
              </div>
            )}
            <div style={{ pointerEvents: "auto", maxWidth: "100%" }}>
              <TaskPrompt
                phase={phase}
                pov={interactionPov}
                enabled={dialogueDone}
                emitCount={emitCount}
                emitsNeeded={game.current.emitsNeeded}
                aligned={aligned}
                pullPct={pullPct}
                descentActive={game.current.descentActive}
                aimNorm={aimNorm}
                handlers={handlers}
              />
            </div>
            {currentLine && (
              <button
                data-testid="hide-text-toggle"
                onClick={() => setHideText((h) => !h)}
                className="ghost-btn hud-touch"
                style={{ padding: "4px 12px", borderRadius: 999, fontSize: 9.5, letterSpacing: "0.08em", cursor: "pointer", pointerEvents: "auto", opacity: 0.75 }}
              >
                {hideText ? "Show Text" : "Hide Text"}
              </button>
            )}
          </div>

          {/* ===== BOTTOM-RIGHT region: one attached panel — Smoke · Breath · Stamina ===== */}
          <div className="hud-region-br" style={{ pointerEvents: "auto" }}>
            <VitalityMeters
              breath={breath}
              stamina={stamina}
              smoke={tourMode ? undefined : smokeDensity}
              onSmoke={(v) => { manualSmokeRef.current = true; setSmokeDensity(v); }}
            />
          </div>
        </div>
      )}

      {screen === "play" && gate && (
        <NarrativeGate
          gate={gate}
          officeImg={OFFICE_IMG}
          auto={tourMode}
          onComplete={closeGate}
          onHaptic={triggerHaptic}
          onDialTone={() => audioRef.current.ring()}
        />
      )}

      {screen === "legacy" && (
        <LegacyScreen remaining={remainingRef.current} onReplay={replay} onFactCheck={() => setFactOpen(true)} />
      )}
      {screen === "fail" && <FailScreen reason={failReason} onReplay={replay} />}

      <FactCheckPanel open={factOpen} onClose={() => setFactOpen(false)} entries={biblio} />
    </div>
  );
}
